import React from 'react';
import useSWR from 'swr';
import { useApi } from '../hooks/useApi';
import ReactMarkdown from 'react-markdown';
import { 
    Box, 
    CircularProgress, 
    Alert, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Typography,
    Paper,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    styled,
    alpha,
    Fade
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { cyber } from '../theme';

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: theme.spacing(2),
    background: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
    overflow: 'hidden',
    '& .MuiTable-root': {
        borderCollapse: 'separate',
        borderSpacing: 0,
    }
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    '& .MuiTableRow-root': {
        background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
    },
    '& .MuiTableCell-root': {
        fontWeight: 700,
        fontSize: '0.95rem',
        color: theme.palette.primary.main,
        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        padding: theme.spacing(2),
    }
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
    '& .MuiTableRow-root': {
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            background: alpha(theme.palette.primary.main, 0.05),
        },
        '&:nth-of-type(odd)': {
            background: alpha(theme.palette.background.default, 0.3),
        },
        '&:last-child td': {
            borderBottom: 0,
        }
    },
    '& .MuiTableCell-root': {
        padding: theme.spacing(2),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        verticalAlign: 'top',
        lineHeight: 1.7,
        '& p, & span': {
            margin: 0,
        },
        '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline',
            }
        },
        '& code': {
            background: alpha(theme.palette.primary.main, 0.1),
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.85rem',
            fontFamily: cyber.fontMono,
            color: alpha(cyber.accent.cyan, 0.9),
        }
    }
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
    background: alpha(theme.palette.background.paper, 0.6),
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
    background: alpha(theme.palette.background.paper, 0.6),
    borderRadius: theme.spacing(2),
    border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
    textAlign: 'center',
}));

// --- Canonical 5W model (decouples rendering from the stored format) ---
interface FiveWReference {
    label: string;
    url: string;
}

interface FiveWItem {
    id: string;
    label: string;
    icon: string;
    color: string;
    answer: string;
    confidence?: string;
    references?: FiveWReference[];
    notApplicable: boolean;
}

// Question definitions ordered by matching priority so that ambiguous labels
// (e.g. "So What?", "What is Next?") are detected before the plain "What?".
const QUESTION_DEFS: Array<{ id: string; label: string; icon: string; color: string; test: (s: string) => boolean }> = [
    { id: 'whatNext', label: 'What is Next?', icon: '⏭️', color: cyber.accent.green, test: (s) => s.includes('next') },
    { id: 'soWhat', label: 'So What?', icon: '💥', color: cyber.accent.coral, test: (s) => s.includes('so what') || s.includes('implication') || s.includes('impact') },
    { id: 'references', label: 'References', icon: '🔗', color: cyber.primary, test: (s) => s.includes('reference') || s.includes('source') },
    { id: 'who', label: 'Who?', icon: '👤', color: cyber.primary, test: (s) => s.includes('who') },
    { id: 'when', label: 'When?', icon: '🕐', color: cyber.accent.green, test: (s) => s.includes('when') },
    { id: 'where', label: 'Where?', icon: '📍', color: cyber.accent.cyan, test: (s) => s.includes('where') },
    { id: 'why', label: 'Why?', icon: '❓', color: cyber.accent.amber, test: (s) => s.includes('why') },
    { id: 'how', label: 'How?', icon: '⚙️', color: cyber.accent.coral, test: (s) => s.includes('how') },
    { id: 'what', label: 'What?', icon: '📋', color: cyber.primaryLight, test: (s) => s.includes('what') },
];

const matchQuestion = (rawLabel: string): { id: string; label: string; icon: string; color: string } => {
    const s = rawLabel.toLowerCase().replace(/[^a-z ]/g, ' ').replace(/\s+/g, ' ').trim();
    const def = QUESTION_DEFS.find(d => d.test(s));
    if (def) {
        return { id: def.id, label: def.label, icon: def.icon, color: def.color };
    }
    // Unknown label: keep it verbatim with a neutral style (forward-compatible).
    return { id: rawLabel, label: rawLabel.replace(/\*/g, '').trim() || 'Detail', icon: '•', color: cyber.primary };
};

const NA_PATTERN = /^(not applicable|n\/?a|information not available[^|]*)$/i;

const buildItem = (rawLabel: string, answer: string, confidence?: string, references?: FiveWReference[]): FiveWItem => {
    const meta = matchQuestion(rawLabel);
    const cleanAnswer = (answer || '').trim();
    return {
        ...meta,
        answer: cleanAnswer,
        confidence: confidence?.trim() || undefined,
        references: references && references.length ? references : undefined,
        notApplicable: NA_PATTERN.test(cleanAnswer.replace(/\*/g, '').trim()),
    };
};

// --- Parser for the NEW format (structured JSON) ---
const fromJson = (data: unknown): FiveWItem[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const arr: any[] = Array.isArray(data)
        ? data
        : (data && typeof data === 'object' && Array.isArray((data as { items?: unknown[] }).items))
            ? (data as { items: unknown[] }).items
            : [];
    return arr
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((it: any): FiveWItem | null => {
            if (!it || typeof it !== 'object') return null;
            const label = String(it.question ?? it.label ?? it.id ?? '').trim();
            const answer = String(it.answer ?? it.summary ?? it.value ?? '').trim();
            if (!label && !answer) return null;
            const refs: FiveWReference[] | undefined = Array.isArray(it.references)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ? it.references
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .map((r: any): FiveWReference => (typeof r === 'string'
                        ? { label: r, url: r }
                        : { label: String(r?.label ?? r?.url ?? '').trim(), url: String(r?.url ?? '').trim() }))
                    .filter((r: FiveWReference) => r.url || r.label)
                : undefined;
            return buildItem(label || 'Detail', answer, it.confidence ? String(it.confidence) : undefined, refs);
        })
        .filter((x: FiveWItem | null): x is FiveWItem => x !== null);
};

// --- Parser for the LEGACY format (Markdown table) — backward compatible ---
const isSeparatorRow = (cells: string[]): boolean =>
    cells.length > 0 && cells.every(c => /^:?-{1,}:?$/.test(c.replace(/\s/g, '')) || c === '');

const splitRow = (line: string): string[] => {
    let cells = line.split('|').map(c => c.trim());
    // Drop only the empty artifacts created by leading/trailing pipes.
    if (cells.length && cells[0] === '') cells = cells.slice(1);
    if (cells.length && cells[cells.length - 1] === '') cells = cells.slice(0, -1);
    return cells;
};

const fromMarkdownTable = (markdown: string): FiveWItem[] => {
    if (!markdown) return [];
    // Strip code fences the LLM may have wrapped around the table.
    const cleaned = markdown.replace(/```[a-zA-Z]*\n?/g, '').trim();
    const rows = cleaned
        .split('\n')
        .map(l => l.trim())
        .filter(l => l.startsWith('|'))
        .map(splitRow)
        .filter(cells => cells.length > 0 && !isSeparatorRow(cells));

    if (rows.length === 0) return [];

    // Legacy layout is a 2-column vertical table: | Question | Summary |.
    // The first row is the header; the data rows follow.
    const dataRows = rows.slice(1);
    return dataRows
        .map((cells): FiveWItem | null => {
            const label = cells[0] ?? '';
            const answer = cells.slice(1).join(' ').trim();
            if (!label && !answer) return null;
            return buildItem(label, answer);
        })
        .filter((x: FiveWItem | null): x is FiveWItem => x !== null);
};

// --- Format-agnostic normalization (the heart of backward compatibility) ---
const normalizeFiveWhats = (raw: string): FiveWItem[] => {
    const t = (raw || '').trim();
    if (!t) return [];
    if (t.startsWith('{') || t.startsWith('[')) {
        try {
            return fromJson(JSON.parse(t));
        } catch {
            // Not valid JSON after all — fall through to markdown parsing.
        }
    }
    return fromMarkdownTable(t);
};

const confidenceColor = (c?: string): string => {
    switch ((c || '').toLowerCase()) {
        case 'high': return cyber.accent.green;
        case 'medium': return cyber.accent.amber;
        case 'low': return cyber.accent.coral;
        default: return cyber.primary;
    }
};

// Inline markdown with secure external links (target=_blank + noopener).
// `block` keeps paragraphs for the raw fallback; otherwise paragraphs render
// as spans to fit inside table cells / card bodies.
const SecureMarkdown: React.FC<{ children: string; block?: boolean }> = ({ children, block }) => (
    <ReactMarkdown
        components={{
            ...(block ? {} : { p: 'span' as const }),
            a: ({ href, children: linkChildren }) => (
                <a href={href} target="_blank" rel="noopener noreferrer">{linkChildren}</a>
            ),
        }}
    >
        {children}
    </ReactMarkdown>
);

// Card used by the default (responsive) view.
const QuestionCard = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'accent',
})<{ accent: string }>(({ theme, accent }) => ({
    position: 'relative',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    background: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(accent, 0.25)}`,
    borderLeft: `4px solid ${accent}`,
    transition: 'all 0.2s ease-in-out',
    height: '100%',
    '&:hover': {
        borderColor: alpha(accent, 0.5),
        boxShadow: `0 4px 24px ${alpha(accent, 0.18)}`,
    },
    '& a': {
        color: accent,
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
    },
    '& code': {
        background: alpha(accent, 0.1),
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.85rem',
        fontFamily: cyber.fontMono,
        color: alpha(cyber.accent.cyan, 0.9),
    },
}));

interface FiveWhatsTableProps {
    fiveWhatsUrl?: string;
}

type ViewMode = 'cards' | 'table';

const FiveWhatsTable: React.FC<FiveWhatsTableProps> = ({ fiveWhatsUrl }) => {
    const { simpleTextFetcher } = useApi();
    const [view, setView] = React.useState<ViewMode>('cards');
    const { data: rawData, error, isLoading } = useSWR(
        fiveWhatsUrl,
        simpleTextFetcher
    );

    if (isLoading) {
        return (
            <Fade in timeout={500}>
                <LoadingContainer>
                    <CircularProgress size={48} thickness={4} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Loading Five Whats analysis...
                    </Typography>
                </LoadingContainer>
            </Fade>
        );
    }

    if (error) {
        return (
            <Fade in timeout={500}>
                <Alert 
                    severity="error" 
                    sx={{ 
                        borderRadius: 2,
                        border: `1px solid ${alpha('#ff5c5c', 0.3)}`
                    }}
                >
                    Could not load the 'Five Whats' data. Please try again later.
                </Alert>
            </Fade>
        );
    }

    const raw = rawData || '';
    const items = normalizeFiveWhats(raw);

    // Backward-compatibility safety net: if we could not parse a known
    // structure but there IS content, render it as raw markdown so nothing is
    // ever lost for older / unexpected reports.
    if (items.length === 0) {
        if (raw.trim()) {
            return (
                <Fade in timeout={500}>
                    <StyledTableContainer sx={{ p: 3 }}>
                        <SecureMarkdown block>{raw}</SecureMarkdown>
                    </StyledTableContainer>
                </Fade>
            );
        }
        return (
            <Fade in timeout={500}>
                <EmptyStateBox>
                    <HelpOutlineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Data Available
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                        The 'Five Whats' analysis is not available for this report.
                    </Typography>
                </EmptyStateBox>
            </Fade>
        );
    }

    return (
        <Fade in timeout={500}>
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <ToggleButtonGroup
                        size="small"
                        value={view}
                        exclusive
                        onChange={(_e, next: ViewMode | null) => { if (next) setView(next); }}
                        aria-label="five whats view mode"
                    >
                        <ToggleButton value="cards" aria-label="card view">
                            <ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} /> Cards
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="table view">
                            <TableRowsIcon fontSize="small" sx={{ mr: 0.5 }} /> Table
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {view === 'cards' ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(auto-fill, minmax(320px, 1fr))',
                            },
                        }}
                    >
                        {items.map((item, index) => (
                            <QuestionCard key={`${item.id}-${index}`} accent={item.color} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Box component="span" sx={{ fontSize: '1.1rem' }}>{item.icon}</Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: item.color, flexGrow: 1 }}>
                                        {item.label}
                                    </Typography>
                                    {item.confidence && (
                                        <Chip
                                            label={item.confidence}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                color: confidenceColor(item.confidence),
                                                bgcolor: alpha(confidenceColor(item.confidence), 0.12),
                                                border: `1px solid ${alpha(confidenceColor(item.confidence), 0.4)}`,
                                            }}
                                        />
                                    )}
                                </Box>

                                {item.notApplicable ? (
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
                                        Not applicable
                                    </Typography>
                                ) : (
                                    <Box sx={{ '& span, & p': { m: 0 }, lineHeight: 1.7, color: 'text.secondary' }}>
                                        <SecureMarkdown>{item.answer || '—'}</SecureMarkdown>
                                    </Box>
                                )}

                                {item.references && item.references.length > 0 && (
                                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {item.references.map((ref, i) => (
                                            <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem' }}>
                                                {ref.label || ref.url}
                                            </a>
                                        ))}
                                    </Box>
                                )}
                            </QuestionCard>
                        ))}
                    </Box>
                ) : (
                    <StyledTableContainer>
                        <Table aria-label="five whats table">
                            <StyledTableHead>
                                <TableRow>
                                    <TableCell>Question</TableCell>
                                    <TableCell>Summary</TableCell>
                                </TableRow>
                            </StyledTableHead>
                            <StyledTableBody>
                                {items.map((item, index) => (
                                    <TableRow key={`${item.id}-${index}`}>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: item.color }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <span>{item.icon}</span>
                                                {item.label}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {item.notApplicable ? (
                                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
                                                    Not applicable
                                                </Typography>
                                            ) : (
                                                <SecureMarkdown>{item.answer || '—'}</SecureMarkdown>
                                            )}
                                            {item.references && item.references.length > 0 && (
                                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {item.references.map((ref, i) => (
                                                        <a key={i} href={ref.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem' }}>
                                                            {ref.label || ref.url}
                                                        </a>
                                                    ))}
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </StyledTableBody>
                        </Table>
                    </StyledTableContainer>
                )}
            </Box>
        </Fade>
    );
};

export default FiveWhatsTable;